/**
 * I18n — tiny localization helper.
 *
 * Locale files are JSON in `assets/locales/<lang>.json`. Lookup is a simple
 * dotted-key, with `{name}` interpolation. Missing keys fall back through
 * the configured fallback chain (default: configured language → 'en' → key).
 */
import { platformStorage } from '../save/Storage.js';

export class I18n {
  constructor() {
    /** @type {Map<string, object>} */
    this._dicts = new Map();
    this.lang = platformStorage.get('language', this._detectLanguage());
    this.fallbackLang = 'en';
  }

  _detectLanguage() {
    const nav = (typeof navigator !== 'undefined' && (navigator.language || navigator.userLanguage)) || 'en';
    return nav.split('-')[0];
  }

  /** Register a parsed locale dict (called by the SDK Preload scene). */
  registerDict(lang, dict) {
    this._dicts.set(lang, dict || {});
  }

  setLanguage(lang) {
    this.lang = lang;
    platformStorage.set('language', lang);
  }

  getAvailableLanguages() {
    return Array.from(this._dicts.keys());
  }

  /**
   * Translate a key. Supports `{name}` interpolation.
   * Falls back to the fallback language, then to the explicit default
   * value (if provided), then to the literal key.
   *
   * Three calling shapes:
   *   t('menu.play')                            → 'menu.play' if missing
   *   t('menu.play', 'Play')                    → 'Play' if missing
   *   t('greet', { name: 'Alice' })             → 'greet' if missing (params interpolated)
   *   t('greet', { name: 'Alice' }, 'Hello {name}')  → 'Hello Alice' if missing
   *
   * The second-arg-as-string overload exists so SDK shell scenes can write
   * `i18n.t('menu.play', 'Play')` and get a sensible English fallback when
   * no locale file has been loaded yet — instead of the literal key
   * 'menu.play' leaking into the UI.
   */
  t(key, paramsOrDefault, defaultValue) {
    let params = null;
    let def = null;
    if (typeof paramsOrDefault === 'string') {
      def = paramsOrDefault;
    } else if (paramsOrDefault && typeof paramsOrDefault === 'object') {
      params = paramsOrDefault;
      if (typeof defaultValue === 'string') def = defaultValue;
    }

    const tryLang = (lang) => {
      const dict = this._dicts.get(lang);
      if (!dict) return undefined;
      const parts = key.split('.');
      let cur = dict;
      for (const p of parts) {
        if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
        else return undefined;
      }
      return typeof cur === 'string' ? cur : undefined;
    };

    let str = tryLang(this.lang);
    if (str == null) str = tryLang(this.fallbackLang);
    if (str == null) str = def;
    if (str == null) str = key;

    if (params) {
      str = str.replace(/\{(\w+)\}/g, (_, k) => (k in params ? String(params[k]) : '{' + k + '}'));
    }
    return str;
  }
}
